import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppData } from '@shared/models/appdata.model';
import { Observable } from 'rxjs';
import {
  AdjudicatedInsuranceClaim,
  DamageReason,
  GradingQuestion,
  InsuranceEligibility,
  QuestionAnswerPair,
} from '../modules/mobilephone/models/equipment-api.model';
import { DamageDetails } from '../modules/mobilephone/models/equipment-upgrade-agreement.model';
import { ImeiValidation, InformationDisclosure, TradeInInformation } from '../modules/mobilephone/models/trade-in-api.model';
import { AcceptTradeInQuoteResponse, TradeInRequest, TradeInResponse } from '../models/trade-in-product.model';
import { GradingQuestion as TradeInGradingQuestion } from '../modules/mobilephone/models/trade-in-api.model';

@Injectable({
  providedIn: 'root',
})
export class EquipmentService {
  private http = inject(HttpClient);
  private appData = inject(AppData);

  apiUrl = this.appData.apiUrl + '/api/v1';

  getGradingQuestions(): Observable<GradingQuestion[]> {
    const apiUrl = `${this.apiUrl}/equipment/redemption/grading-questions`;
    return this.http.get<GradingQuestion[]>(apiUrl);
  }

  refreshEquipments(customerId: string): Observable<boolean> {
    const url = `${this.appData.apiUrl}/api/v1/equipment/${customerId}/refresh-equipments`;
    return this.http.post<boolean>(url, null);
  }

  validateImei(agreementId: string, imei: string): Observable<boolean> {
    const apiUrl = `${this.apiUrl}/equipment/redemption/validate-imei?agreementId=${agreementId}`;
    return this.http.get<boolean>(apiUrl, { headers: { 'cache-control': 'no-cache', imei: imei } });
  }

  initializeRedemption(agreementId: string | number, isMobileReturnedInStore: boolean): Observable<number> {
    const url = `${this.appData.apiUrl}/api/v1/equipment/redemption`;
    return this.http.post<number>(url, {
      agreementId: agreementId.toString(),
      isMobileReturnedInStore,
    });
  }

  sendGradingAnswers(
    redemptionId: number,
    imei: string,
    questionAnswerPair: QuestionAnswerPair[]
  ): Observable<InsuranceEligibility> {
    const url = `${this.appData.apiUrl}/api/v1/equipment/redemption/${redemptionId}/grading-answers`;
    return this.http.post<InsuranceEligibility>(url, {
      imei,
      gradingQuestionAnswerPairs: questionAnswerPair,
    });
  }

  createRedemptionClaim(
    redemptionId: number,
    imei: string,
    customerEmail: string,
    damage: DamageDetails
  ): Observable<AdjudicatedInsuranceClaim> {
    const url = `${this.appData.apiUrl}/api/v1/equipment/redemption/${redemptionId}/claim`;
    return this.http.post<AdjudicatedInsuranceClaim>(url, {
      imei,
      damageDate: damage.damageDate,
      damageDescription: damage.damageReasonText,
      damageReasonCode: damage.damageReason,
      customerEmail,
    });
  }

  getDamageReasons(): Observable<DamageReason[]> {
    const apiUrl = `${this.apiUrl}/equipment/redemption/damage-reasons`;
    return this.http.get<DamageReason[]>(apiUrl);
  }

  getInformationDisclosure(informationKey: string) {
    const apiUrl = `${this.apiUrl}/equipment/tradein/information-disclosure?informationKey=${informationKey}`;
    return this.http.get<InformationDisclosure>(apiUrl);
  }

  acceptQuote(tradeInId: number): Observable<AcceptTradeInQuoteResponse> {
    const apiUrl = `${this.apiUrl}/equipment/tradein/${tradeInId}`;
    return this.http.post<AcceptTradeInQuoteResponse>(apiUrl, {});
  }

  getGradingQuestionsTradeIn(): Observable<TradeInGradingQuestion[]> {
    const apiUrl = `${this.apiUrl}/equipment/tradein/grading-questions`;
    return this.http.get<TradeInGradingQuestion[]>(apiUrl);
  }

  createTradeInVoucher(tradeInRequest: TradeInRequest): Observable<TradeInResponse> {
    const apiUrl = `${this.apiUrl}/equipment/tradein`;
    return this.http.post<TradeInResponse>(apiUrl, tradeInRequest);
  }

  getInformation(informationKey: string) {
    const apiUrl = `${this.apiUrl}/equipment/tradein/information?informationKey=${informationKey}`;
    return this.http.get<TradeInInformation>(apiUrl);
  }

  validateImeiTradeIn(imei: string): Observable<ImeiValidation> {
    const apiUrl = `${this.apiUrl}/equipment/tradein/validate-imei?imei=${imei}`;
    return this.http.get<ImeiValidation>(apiUrl);
  }
}
